import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { getAiUsageDashboard } from '../../services/api/implementation/impl';
import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import toast from 'react-hot-toast';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { getFixedHeaderClass } from '../../../shared/utils/sidebarLayout';

function StatCard({ label, value, accent = 'teal' }) {
  const colors =
    accent === 'amber'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-teal-100 bg-teal-50/60 text-[#1E4A53]';
  return (
    <div className={`rounded-2xl border px-5 py-4 ${colors}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold">{Number(value).toLocaleString('en-IN')}</p>
    </div>
  );
}

const AiUsageDashboard = () => {
  const { collapsed } = useSidebar();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['aiUsageDashboard'],
    queryFn: getAiUsageDashboard,
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Failed to load AI usage');
    }
  });

  const chartData = (data?.tokensLast30Days ?? []).map((d) => ({
    ...d,
    label: d.day.slice(5)
  }));

  return (
    <Layout>
      <Loader isVisible={isLoading} />
      <div className={`${getFixedHeaderClass(collapsed)} z-10 bg-gray-100`}>
        <Header title="AI Usage" />
      </div>

      <div className="p-6 pt-24">
        {isError ? (
          <p className="text-sm text-red-600">Could not load AI usage data.</p>
        ) : (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              <StatCard label="Tokens used (this month)" value={data?.totals?.consumedThisMonth ?? 0} />
              <StatCard
                label="Tokens recharged (this month)"
                value={data?.totals?.rechargedThisMonth ?? 0}
              />
              <StatCard
                label="Pending recharge requests"
                value={data?.totals?.pendingRechargeRequests ?? 0}
                accent="amber"
              />
            </div>

            <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-bold text-gray-900">Daily token usage (30 days)</h3>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="tokens" fill="#1E4A53" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 text-sm font-bold text-gray-900">Top builders (this month)</h3>
                <ul className="space-y-2 text-sm">
                  {(data?.topBuilders ?? []).length === 0 ? (
                    <li className="text-gray-500">No usage recorded yet.</li>
                  ) : (
                    data.topBuilders.map((b) => (
                      <li
                        key={b.builderId}
                        className="flex justify-between border-b border-gray-100 py-2 last:border-0">
                        <span>{b.builderName || `Builder #${b.builderId}`}</span>
                        <span className="font-semibold text-[#1E4A53]">
                          {b.tokens.toLocaleString('en-IN')}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 text-sm font-bold text-gray-900">By feature (30 days)</h3>
                <ul className="space-y-2 text-sm">
                  {(data?.tokensByFeature ?? []).map((f) => (
                    <li
                      key={f.feature}
                      className="flex justify-between border-b border-gray-100 py-2 last:border-0">
                      <span className="font-mono text-xs">{f.feature}</span>
                      <span className="font-semibold">{f.tokens.toLocaleString('en-IN')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {(data?.rechargeRequests ?? []).length > 0 ? (
              <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
                <h3 className="mb-3 text-sm font-bold text-amber-950">Pending recharge requests</h3>
                <ul className="space-y-3 text-sm">
                  {data.rechargeRequests.map((r) => (
                    <li
                      key={r.id}
                      className="rounded-xl border border-amber-200 bg-white px-4 py-3">
                      <p className="font-semibold text-gray-900">
                        {r.builderName || `Builder #${r.builderId}`}
                      </p>
                      <p className="text-xs text-gray-600">
                        {r.userName} ·{' '}
                        {r.requestedTokens
                          ? `${r.requestedTokens.toLocaleString('en-IN')} tokens requested`
                          : 'Amount not specified'}
                      </p>
                      {r.message ? <p className="mt-1 text-xs text-gray-700">{r.message}</p> : null}
                      <p className="mt-1 text-[10px] text-gray-500">
                        {new Date(r.createdAt).toLocaleString()}
                      </p>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-amber-900">
                  Recharge from User Management → Unit Stats → sparkle (AI credits) icon.
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AiUsageDashboard;
